/**
 * SiteList page — searchable, sortable, filterable DataTable of sites.
 * Defaults to showing only Branch-type sites. SiteType filter replaces
 * the old Meraki deployment status filter.
 *
 * Requirements: 32.4
 */

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { siteService, type SiteListParams } from '@/services/siteService';
import type { SiteLocation, SortDirection } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

export function SiteList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.isAdmin ||
    user?.roles.some((r) => ['Admin', 'Contract Specialist'].includes(r));

  const [params, setParams] = useState<SiteListParams>({
    page: 1,
    pageSize: 25,
    sortBy: 'siteNumber',
    sortDir: 'asc',
    isActive: true,
    siteType: 'Branch',
  });

  const [stateFilter, setStateFilter] = useState<string>('');
  const [siteTypeFilter, setSiteTypeFilter] = useState<string>('Branch');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sites', params, stateFilter, siteTypeFilter],
    queryFn: () =>
      siteService.list({
        ...params,
        state: stateFilter || undefined,
        siteType: siteTypeFilter || undefined,
      }),
  });

  const handleSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleSort = useCallback((key: string, direction: SortDirection) => {
    setParams((prev) => ({ ...prev, sortBy: key, sortDir: direction }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const columns: Column<SiteLocation & Record<string, unknown>>[] = [
    {
      key: 'siteNumber',
      header: 'Site #',
      sortable: true,
      width: '80px',
      render: (row) => (
        <Link
          to={`/sites/${row.siteNumber}`}
          className="text-baker-red no-underline font-medium hover:underline"
        >
          {row.siteNumber}
        </Link>
      ),
    },
    {
      key: 'branchName',
      header: 'Name',
      sortable: true,
      render: (row) => row.branchName,
    },
    {
      key: 'city',
      header: 'City',
      sortable: false,
      render: (row) => row.city ?? '—',
    },
    {
      key: 'state',
      header: 'State',
      sortable: true,
      width: '70px',
      render: (row) => row.state ?? '—',
    },
    {
      key: 'divisionName',
      header: 'Division',
      sortable: true,
      render: (row) => row.divisionName ?? '—',
    },
    {
      key: 'regionCode',
      header: 'Region',
      sortable: true,
      width: '80px',
      render: (row) => row.regionCode ?? '—',
    },
    {
      key: 'regionName',
      header: 'Regional Manager',
      sortable: true,
      render: (row) => row.regionName ?? '—',
    },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="flex justify-between items-center mb-5">
        <h1 className="m-0 text-lg font-semibold text-text-primary">Sites</h1>
        <div className="flex gap-2">
          {canWrite && (
            <>
              <button
                onClick={() => navigate('/sites/new')}
                className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium cursor-pointer hover:bg-baker-red/90 transition-all"
              >
                + Add Site
              </button>
              <button
                onClick={() => navigate('/admin/import')}
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
              >
                Import Sites
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap items-end">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
          <select
            value={siteTypeFilter}
            onChange={(e) => {
              setSiteTypeFilter(e.target.value);
              setParams((prev) => ({ ...prev, page: 1 }));
            }}
            className="py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          >
            <option value="">All Types</option>
            <option value="Branch">Branches</option>
            <option value="Corporate">Corporate</option>
            <option value="HUB">HUB</option>
            <option value="Regional Replenishment Center">RRC</option>
            <option value="Regional Administration">Regional Admin</option>
            <option value="Divisional Administration">Division Admin</option>
            <option value="Line of Business">Line of Business</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">State</label>
          <input
            type="text"
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value.toUpperCase());
              setParams((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="e.g. FL"
            maxLength={2}
            className="py-2 px-3 rounded-lg border border-border text-sm w-16 bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all placeholder:text-slate-400"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
          <select
            value={params.isActive === undefined ? '' : params.isActive ? 'active' : 'inactive'}
            onChange={(e) => {
              const val = e.target.value;
              setParams((prev) => ({
                ...prev,
                isActive: val === '' ? undefined : val === 'active',
                page: 1,
              }));
            }}
            className="py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {error ? (
        <ErrorMessage
          message={(error as Error).message ?? 'Failed to load sites.'}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <LoadingSpinner message="Loading sites…" />
      ) : (
        <DataTable
          columns={columns}
          data={(data?.data ?? []) as (SiteLocation & Record<string, unknown>)[]}
          rowKey={(row) => row.siteNumber}
          totalItems={data?.total}
          page={params.page}
          pageSize={params.pageSize}
          onPageChange={handlePageChange}
          onSort={handleSort}
          onSearch={handleSearch}
          searchPlaceholder="Search sites…"
          emptyMessage="No sites found."
        />
      )}
    </div>
  );
}
